import * as React from 'react'

import { cn } from '@/lib/utils'
import { Input, type InputProps } from '@/components/ui/input'

export type InputGroupSize = 'default' | 'sm' | 'xs'

/**
 * Addon text matches the input's text size, so a text prefix like `https://` reads as part of the
 * value. For a text prefix, pad the input by the addon's offset plus the prefix's width in `em`
 * (`pl-[calc(0.625rem+4.16em)]` for `/pages/`), so the value continues right after it at any text size.
 */
type InputGroupSizeStyle = {
  startInput: string
  endInput: string
  startAddon: string
  endAddon: string
}

const inputGroupSizeStyles: Record<InputGroupSize, InputGroupSizeStyle> = {
  default: {
    startInput: 'pl-8',
    endInput: 'pr-10',
    startAddon: 'left-2.5 text-base md:text-sm [&_svg]:size-4',
    endAddon: 'right-2 text-base md:text-sm [&_svg]:size-4',
  },
  sm: {
    startInput: 'pl-6',
    endInput: 'pr-8',
    startAddon: 'left-2 text-sm [&_svg]:size-3.5',
    endAddon: 'right-1.5 text-sm [&_svg]:size-3.5',
  },
  xs: {
    startInput: 'pl-5',
    endInput: 'pr-7',
    startAddon: 'left-1.5 text-xs [&_svg]:size-3',
    endAddon: 'right-1 text-xs [&_svg]:size-3',
  },
}

export type InputGroupProps = Omit<InputProps, 'size'> & {
  /** Applied to the wrapper element; use `inputClassName` to style the input. */
  className?: string
  endAdornment?: React.ReactNode
  inputClassName?: string
  inputRef?: React.Ref<HTMLInputElement>
  size?: InputGroupSize
  startAdornment?: React.ReactNode
}

const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(function InputGroup(
  { className, endAdornment, inputClassName, inputRef, size = 'default', startAdornment, ...props },
  ref
) {
  const resolvedSize = inputGroupSizeStyles[size] ? size : 'default'
  const sizeStyles = inputGroupSizeStyles[resolvedSize]

  return (
    <div
      ref={ref}
      data-slot="input-group"
      data-size={resolvedSize}
      className={cn('relative', className)}
    >
      {startAdornment ? (
        <div
          data-slot="input-group-addon"
          data-side="start"
          className={cn(
            'pointer-events-none absolute top-1/2 z-10 flex -translate-y-1/2 items-center text-muted-foreground [&>*]:pointer-events-auto',
            sizeStyles.startAddon
          )}
        >
          {startAdornment}
        </div>
      ) : null}

      <Input
        ref={inputRef}
        size={resolvedSize}
        className={cn(
          startAdornment && sizeStyles.startInput,
          endAdornment && sizeStyles.endInput,
          inputClassName
        )}
        {...props}
      />

      {endAdornment ? (
        <div
          data-slot="input-group-addon"
          data-side="end"
          className={cn(
            'pointer-events-none absolute top-1/2 z-10 flex -translate-y-1/2 items-center text-muted-foreground [&>*]:pointer-events-auto',
            sizeStyles.endAddon
          )}
        >
          {endAdornment}
        </div>
      ) : null}
    </div>
  )
})

export { InputGroup }
